import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';
import {
    createSubscription,
    fetchSubscriptionCharges,
    formatSubscriptionData,
    validateCPF,
    validateCNPJ
} from '../../services/pagarmeService';
import {
    validateCardNumber,
    getCardBrand
} from '../../services/cardTokenService';
import { fetchAddressByCep, isValidCep } from '../../services/cepService';
import {
    fetchPlanById,
    formatPlanPrice,
    formatPlanInterval,
    getPlanAmount,
    Plan
} from '../../services/planService';
import { logError } from '../../utils/logger';
import InputMask from '../InputMask';
import BoletoPaymentModal from '../BoletoPaymentModal';
import PaymentResultModal from '../PaymentResultModal';
import ToastNotification from '../ToastNotification';
import { SubscriptionFormData, FormErrors, AlertState, PaymentMethod } from '../../types';

const SubscriptionForm: React.FC = () => {
    // Pega o planId da URL (se existe)
    const { planId: urlPlanId } = useParams<{ planId?: string }>();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState<SubscriptionFormData>({
        // Método de Pagamento
        paymentMethod: 'credit_card',

        // Dados do Cliente
        customerName: '',
        customerEmail: '',
        customerDocument: '',
        phoneAreaCode: '',
        phoneNumber: '',

        // Endereço
        addressZipCode: '',
        addressStreet: '',
        addressNumber: '',
        addressComplement: '',
        addressNeighborhood: '',
        addressCity: '',
        addressState: '',

        // Cartão de Crédito (opcional)
        cardNumber: '',
        cardHolderName: '',
        cardExpMonth: '',
        cardExpYear: '',
        cardCvv: '',
        installments: 1,

        // Plano
        planId: ''
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingCep, setLoadingCep] = useState<boolean>(false);
    const [loadingPlans, setLoadingPlans] = useState<boolean>(false);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [alert, setAlert] = useState<AlertState>({
        show: false,
        type: 'success',
        message: ''
    });
    const [cardBrand, setCardBrand] = useState<string>('');

    // Estados para modais de pagamento
    const [showBoletoModal, setShowBoletoModal] = useState(false);
    const [showPaymentResultModal, setShowPaymentResultModal] = useState(false);
    const [paymentModalData, setPaymentModalData] = useState<any>(null);

    // Carrega o plano pré-selecionado via URL
    useEffect(() => {
        const loadPlans = async () => {
            setLoadingPlans(true);

            if (!urlPlanId) {
                // Acesso sem planId — redireciona para home
                navigate('/', { replace: true });
                return;
            }

            try {
                const result = await fetchPlanById(urlPlanId);
                    
                if (result.success && result.data && result.data.status !== 'deleted') {
                    const plan = result.data;
                    setPlans([plan]);
                    setSelectedPlan(plan);
                        
                    // Pré-seleciona o plano no formulário
                    const firstPaymentMethod = plan.payment_methods?.[0] as PaymentMethod || 'credit_card';
                    const defaultInstallments = plan.installments?.[0] || 1;
                        
                    setFormData(prev => ({
                        ...prev,
                        planId: plan.id,
                        paymentMethod: firstPaymentMethod,
                        installments: defaultInstallments
                    }));
                        
                } else {
                    logError('Plano não encontrado ou inativo', { plan_id: urlPlanId });
                    setAlert({
                        show: true,
                        type: 'danger',
                        message: 'Este plano não está mais disponível. Redirecionando...'
                    });
                    setTimeout(() => navigate('/', { replace: true }), 3000);
                }
            } catch (error) {
                logError('Erro ao carregar plano', { error });
                setAlert({
                    show: true,
                    type: 'danger',
                    message: 'Erro ao carregar o plano. Por favor, tente novamente.'
                });
            } finally {
                setLoadingPlans(false);
            }
        };

        loadPlans();
    }, [urlPlanId, navigate]);

    /**
     * Formata número de cartão de crédito (adiciona espaços a cada 4 dígitos)
     */
    const formatCardNumber = (value: string): string => {
        const cleanValue = value.replace(/\s/g, ''); // Remove espaços
        const matches = cleanValue.match(/.{1,4}/g); // Separa em grupos de 4
        return matches ? matches.join(' ') : cleanValue;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;

        let formattedValue = value;

        // Aplica formatação de cartão de crédito
        if (name === 'cardNumber') {
            // Remove tudo que não é número
            const numbersOnly = value.replace(/\D/g, '');
            // Limita a 16 dígitos
            const limited = numbersOnly.slice(0, 16);
            // Formata com espaços
            formattedValue = formatCardNumber(limited);
            // Identifica a bandeira do cartão
            setCardBrand(getCardBrand(formattedValue));
        }

        // Apenas números para mês de validade (01-12)
        if (name === 'cardExpMonth') {
            const numbersOnly = value.replace(/\D/g, '');
            formattedValue = numbersOnly.slice(0, 2);
        }

        // Apenas números para ano de validade (4 dígitos)
        if (name === 'cardExpYear') {
            const numbersOnly = value.replace(/\D/g, '');
            formattedValue = numbersOnly.slice(0, 4);
        }

        // Apenas números para CVV (3 ou 4 dígitos)
        if (name === 'cardCvv') {
            const numbersOnly = value.replace(/\D/g, '');
            formattedValue = numbersOnly.slice(0, 4);
        }

        // Máscara dinâmica CPF/CNPJ
        if (name === 'customerDocument') {
            const digits = value.replace(/\D/g, '').slice(0, 14);
            if (digits.length <= 11) {
                formattedValue = digits
                    .replace(/(\d{3})(\d)/, '$1.$2')
                    .replace(/(\d{3})(\d)/, '$1.$2')
                    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
            } else {
                formattedValue = digits
                    .replace(/(\d{2})(\d)/, '$1.$2')
                    .replace(/(\d{3})(\d)/, '$1.$2')
                    .replace(/(\d{3})(\d)/, '$1/$2')
                    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: formattedValue
        }));

        // Busca endereço automaticamente quando o CEP é preenchido
        if (name === 'addressZipCode' && isValidCep(value)) {
            handleCepLookup(value);
        }

        // Limpa o erro do campo ao editar
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleCepLookup = async (cep: string): Promise<void> => {
        setLoadingCep(true);

        try {
            const addressData = await fetchAddressByCep(cep);

            if (addressData) {
                setFormData(prev => ({
                    ...prev,
                    addressStreet: addressData.street || prev.addressStreet,
                    addressNeighborhood: addressData.neighborhood || prev.addressNeighborhood,
                    addressCity: addressData.city || prev.addressCity,
                    addressState: addressData.state || prev.addressState
                }));

            } else {
                // CEP não encontrado
            }
        } catch (error) {
            logError('Erro ao buscar CEP', { cep, error });
        } finally {
            setLoadingCep(false);
        }
    };

    // Verifica se todos os campos obrigatórios estão preenchidos
    const isFormComplete = (): boolean => {
        // Dados do cliente
        if (!formData.customerName.trim()) return false;
        if (!formData.customerEmail.trim()) return false;
        if (!formData.customerDocument.trim()) return false;
        if (!formData.phoneAreaCode.trim()) return false;
        if (!formData.phoneNumber.trim()) return false;

        // Endereço
        if (!formData.addressZipCode.trim()) return false;
        if (!formData.addressStreet.trim()) return false;
        if (!formData.addressNumber.trim()) return false;
        if (!formData.addressNeighborhood.trim()) return false;
        if (!formData.addressCity.trim()) return false;
        if (!formData.addressState.trim()) return false;

        // Cartão (apenas para credit_card)
        if (formData.paymentMethod === 'credit_card') {
            if (!formData.cardNumber.trim()) return false;
            if (!formData.cardHolderName.trim()) return false;
            if (!formData.cardExpMonth.trim()) return false;
            if (!formData.cardExpYear.trim()) return false;
            if (!formData.cardCvv.trim()) return false;
        }

        // Plano selecionado
        if (!formData.planId.trim()) return false;

        return true;
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        // Validação de dados do cliente
        if (!formData.customerName.trim()) {
            newErrors.customerName = 'Nome completo é obrigatório';
        }

        if (!formData.customerEmail.trim()) {
            newErrors.customerEmail = 'E-mail é obrigatório';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)) {
            newErrors.customerEmail = 'E-mail inválido';
        }

        const document = formData.customerDocument.replace(/\D/g, '');
        if (!document) {
            newErrors.customerDocument = 'CPF/CNPJ é obrigatório';
        } else if (document.length === 11) {
            if (!validateCPF(document)) {
                newErrors.customerDocument = 'CPF inválido';
            }
        } else if (document.length === 14) {
            if (!validateCNPJ(document)) {
                newErrors.customerDocument = 'CNPJ inválido';
            }
        } else {
            newErrors.customerDocument = 'CPF/CNPJ inválido';
        }

        if (!formData.phoneAreaCode || !formData.phoneNumber) {
            newErrors.phoneNumber = 'Telefone é obrigatório';
        }

        // Validação de endereço
        if (!formData.addressZipCode) {
            newErrors.addressZipCode = 'CEP é obrigatório';
        }

        if (!formData.addressStreet) {
            newErrors.addressStreet = 'Rua é obrigatória';
        }

        if (!formData.addressNumber) {
            newErrors.addressNumber = 'Número é obrigatório';
        }

        if (!formData.addressNeighborhood) {
            newErrors.addressNeighborhood = 'Bairro é obrigatório';
        }

        if (!formData.addressCity) {
            newErrors.addressCity = 'Cidade é obrigatória';
        }

        if (!formData.addressState) {
            newErrors.addressState = 'Estado é obrigatório';
        }

        // Validação do cartão (para credit_card)
        if (formData.paymentMethod === 'credit_card') {
            if (!formData.cardNumber) {
                newErrors.cardNumber = 'Número do cartão é obrigatório';
            } else if (!validateCardNumber(formData.cardNumber)) {
                newErrors.cardNumber = 'Número do cartão inválido';
            }

            if (!formData.cardHolderName.trim()) {
                newErrors.cardHolderName = 'Nome do titular é obrigatório';
            }

            if (!formData.cardExpMonth || !formData.cardExpYear) {
                newErrors.cardExpMonth = 'Data de validade é obrigatória';
            } else {
                const month = parseInt(formData.cardExpMonth);
                const year = parseInt(formData.cardExpYear);
                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().getMonth() + 1;

                if (month < 1 || month > 12) {
                    newErrors.cardExpMonth = 'Mês inválido';
                } else if (year < currentYear || (year === currentYear && month < currentMonth)) {
                    newErrors.cardExpMonth = 'Cartão expirado';
                }
            }

            if (!formData.cardCvv || formData.cardCvv.length < 3) {
                newErrors.cardCvv = 'CVV inválido';
            }
        }

        // Validação do plano
        if (!formData.planId.trim()) {
            newErrors.planId = 'Selecione um plano';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();

        if (!validateForm()) {
            setAlert({
                show: true,
                type: 'danger',
                message: 'Por favor, corrija os erros no formulário antes de continuar.'
            });
            return;
        }

        setLoading(true);
        setAlert({ show: false, type: 'success', message: '' });

        const submissionStartTime = Date.now();

        try {
            let cardToken: string | undefined;

            // PASSO 1: Tokenizar cartão (se necessário)
            if (formData.paymentMethod === 'credit_card') {
                try {
                    const tokenResponse = await fetch('https://api.pagar.me/core/v5/tokens?appId=' + process.env.REACT_APP_PAGARME_PUBLIC_KEY, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            type: 'card',
                            card: {
                                number: formData.cardNumber.replace(/\s/g, ''),
                                holder_name: formData.cardHolderName,
                                exp_month: parseInt(formData.cardExpMonth),
                                exp_year: parseInt(formData.cardExpYear),
                                cvv: formData.cardCvv
                            }
                        })
                    });

                    if (!tokenResponse.ok) {
                        const errorData = await tokenResponse.json();
                        throw new Error(errorData.message || 'Erro ao tokenizar cartão');
                    }

                    const tokenData = await tokenResponse.json();
                    cardToken = tokenData.id;

                } catch (tokenError: any) {
                    logError('Erro ao tokenizar cartão', {
                        error: tokenError.message
                    });

                    setAlert({
                        show: true,
                        type: 'danger',
                        message: 'Erro ao processar dados do cartão. Verifique as informações e tente novamente.'
                    });
                    setLoading(false);
                    return;
                }
            }

            // PASSO 2: Criar assinatura
            const paymentMessages: Record<string, string> = {
                credit_card: 'Criando assinatura...',
                pix: 'Gerando QR Code PIX...',
                boleto: 'Gerando boleto...'
            };

            setAlert({
                show: true,
                type: 'info',
                message: paymentMessages[formData.paymentMethod]
            });

            const subscriptionData = formatSubscriptionData(formData, cardToken);
            
            const result = await createSubscription(subscriptionData);

            if (result.success && result.data) {
                // Se for boleto, busca a cobrança e exibe modal
                if (formData.paymentMethod === 'boleto') {
                    setAlert({
                        show: true,
                        type: 'info',
                        message: '🔍 Aguardando processamento do boleto...'
                    });

                    const isTestEnv = process.env.REACT_APP_PAGARME_PUBLIC_KEY?.startsWith('pk_test_');
                    let boletoUrl: string | undefined;
                    let dueDate: string | undefined;

                    try {
                        const chargeResult = await fetchSubscriptionCharges(result.data.id);
                        
                        if (chargeResult.success && chargeResult.data) {
                            const chargeData = chargeResult.data;
                            boletoUrl = chargeData.last_transaction?.url;
                            dueDate = chargeData.last_transaction?.due_at;
                            
                            if (boletoUrl) {
                                // boleto encontrado
                            } else {
                                // boleto não encontrado na charge
                            }
                        }
                    } catch (boletoError) {
                        logError('Erro ao buscar boleto', { error: boletoError });
                    }

                    // Sempre exibe modal, mesmo sem boleto
                    setPaymentModalData({
                        boletoUrl,
                        subscriptionId: result.data.id,
                        amount: selectedPlan ? getPlanAmount(selectedPlan) : undefined,
                        dueDate,
                        isTestEnvironment: isTestEnv
                    });
                    setShowBoletoModal(true);
                    setAlert({ show: false, type: 'success', message: '' });
                } else {
                    // Cartão de crédito: exibe modal de resultado
                    const isSuccess = result.data.status === 'active';
                    
                    // Tenta buscar mais detalhes do erro se falhou
                    let errorMessage = isSuccess 
                        ? 'Seu pagamento foi aprovado e sua assinatura está ativa!'
                        : 'O pagamento não foi aprovado. Verifique os dados do cartão ou tente outro método de pagamento.';
                    
                    if (!isSuccess) {
                        try {
                            const chargeResult = await fetchSubscriptionCharges(result.data.id);
                            
                            if (chargeResult.success && chargeResult.data?.last_transaction) {
                                const transaction = chargeResult.data.last_transaction;
                                
                                // Tenta diferentes campos de erro
                                let acquirerMessage = 
                                    transaction.acquirer_message || 
                                    transaction.acquirer_return_code ||
                                    transaction.acquirer_auth_code;
                                
                                // Se gateway_response existir e for objeto
                                if (transaction.gateway_response) {
                                    if (typeof transaction.gateway_response === 'object') {
                                        acquirerMessage = acquirerMessage || 
                                            transaction.gateway_response.code || 
                                            transaction.gateway_response.message ||
                                            (transaction.gateway_response.errors ? 
                                                transaction.gateway_response.errors.join(', ') : null);
                                    } else {
                                        acquirerMessage = acquirerMessage || transaction.gateway_response;
                                    }
                                }
                                
                                // Converte para string se for objeto
                                if (typeof acquirerMessage === 'object') {
                                    acquirerMessage = JSON.stringify(acquirerMessage);
                                }
                                
                                if (acquirerMessage && typeof acquirerMessage === 'string') {
                                    errorMessage = `Cartão recusado: ${acquirerMessage}`;
                                }
                            }
                        } catch (err) {
                            logError('Erro ao buscar detalhes da transação', { error: err });
                        }
                    }
                    
                    setPaymentModalData({
                        success: isSuccess,
                        subscriptionId: result.data.id,
                        status: result.data.status,
                        amount: selectedPlan ? getPlanAmount(selectedPlan) : undefined,
                        installments: formData.installments,
                        message: errorMessage
                    });
                    setShowPaymentResultModal(true);
                    setAlert({ show: false, type: 'success', message: '' });
                }
            } else {
                const errorData = result.error;
                let errorMessage = 'Erro ao criar assinatura. Tente novamente.';

                if (typeof errorData === 'object' && errorData.errors) {
                    errorMessage = Object.values(errorData.errors).flat().join(', ');
                } else if (typeof errorData === 'object' && errorData.message) {
                    errorMessage = errorData.message;
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                }

                logError('Falha ao criar assinatura', {
                    error: result.error,
                    customer_email: formData.customerEmail,
                    plan_id: formData.planId
                });

                setAlert({
                    show: true,
                    type: 'danger',
                    message: `❌ Erro: ${errorMessage}`
                });
            }
        } catch (error: any) {
            logError('Erro inesperado no processo de assinatura', {
                error: error.message,
                stack: error.stack,
                customer_email: formData.customerEmail
            });

            setAlert({
                show: true,
                type: 'danger',
                message: '❌ Erro inesperado ao processar a assinatura. Tente novamente.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="subscription-card my-5">
            <Card.Header className="bg-primary text-white">
                <h4 className="mb-0">Nova Assinatura</h4>
            </Card.Header>
            <Card.Body className="p-4">
                <Form onSubmit={handleSubmit}>
                    {/* Seção de Plano - Diferente se vier pré-selecionado da URL */}
                    {urlPlanId && selectedPlan ? (
                        // Plano pré-selecionado - Apenas exibe resumo
                        <>
                            <h5 className="form-section-title">Plano Selecionado</h5>
                            <Card className="mb-4 border-primary">
                                <Card.Body className="p-4">
                                    <Row className="align-items-center">
                                        <Col>
                                            <h4 className="mb-1">{selectedPlan.name}</h4>
                                            {selectedPlan.description && (
                                                <p className="text-muted mb-0">{selectedPlan.description}</p>
                                            )}
                                        </Col>
                                        <Col xs="auto" className="text-end">
                                            <h3 className="text-primary mb-0">
                                                {formatPlanPrice(getPlanAmount(selectedPlan))}
                                            </h3>
                                            <small className="text-muted d-block">
                                                {formatPlanInterval(selectedPlan.interval, selectedPlan.interval_count)}
                                            </small>
                                            {selectedPlan.installments && selectedPlan.installments.length > 1 && (
                                                <small className="text-muted d-block">
                                                    em até {Math.max(...selectedPlan.installments)}x no cartão
                                                </small>
                                            )}
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </>
                    ) : (
                        // Sem planId na URL — não deve ser acessível, redireciona na montagem
                        null
                    )}

                    {/* Método de Pagamento */}
                    <h5 className="form-section-title">Método de Pagamento</h5>
                    <div className="mb-4">
                        <Row className="g-3">
                            {/* Cartão de Crédito */}
                            {selectedPlan?.payment_methods?.includes('credit_card') && (
                                <Col md={4}>
                                    <Card
                                        className={`payment-method-card h-100 text-center ${formData.paymentMethod === 'credit_card' ? 'border-primary selected-payment' : ''}`}
                                        style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'credit_card' }))}
                                    >
                                        <Card.Body className="d-flex flex-column align-items-center justify-content-center p-4">
                                            <div className="fs-1 mb-2">💳</div>
                                            <h6 className="mb-1">Cartão de Crédito</h6>
                                            {formData.paymentMethod === 'credit_card' && (
                                                <Badge bg="primary" className="mt-2">✓ Selecionado</Badge>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}

                            {/* Boleto */}
                            {selectedPlan?.payment_methods?.includes('boleto') && (
                                <Col md={4}>
                                    <Card
                                        className={`payment-method-card h-100 text-center ${formData.paymentMethod === 'boleto' ? 'border-primary selected-payment' : ''}`}
                                        style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'boleto' }))}
                                    >
                                        <Card.Body className="d-flex flex-column align-items-center justify-content-center p-4">
                                            <div className="fs-1 mb-2">🧾</div>
                                            <h6 className="mb-1">Boleto</h6>
                                            {formData.paymentMethod === 'boleto' && (
                                                <Badge bg="primary" className="mt-2">✓ Selecionado</Badge>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                        {!selectedPlan && (
                            <div className="text-muted small mt-2">
                                ⚠️ Selecione um plano primeiro para ver os métodos de pagamento disponíveis
                            </div>
                        )}
                    </div>

                    {/* Dados do Cliente */}
                    <h5 className="form-section-title">Dados do Cliente</h5>

                    <Form.Group className="mb-3">
                        <Form.Label>Nome Completo *</Form.Label>
                        <Form.Control
                            type="text"
                            name="customerName"
                            value={formData.customerName}
                            onChange={handleChange}
                            isInvalid={!!errors.customerName}
                            placeholder="João da Silva"
                            disabled={loading}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.customerName}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>E-mail *</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="customerEmail"
                                    value={formData.customerEmail}
                                    onChange={handleChange}
                                    isInvalid={!!errors.customerEmail}
                                    placeholder="joao@example.com"
                                    disabled={loading}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.customerEmail}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>CPF/CNPJ *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="customerDocument"
                                    value={formData.customerDocument}
                                    onChange={handleChange}
                                    isInvalid={!!errors.customerDocument}
                                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                                    disabled={loading}
                                    inputMode="numeric"
                                    maxLength={18}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.customerDocument}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>DDD *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="phoneAreaCode"
                                    value={formData.phoneAreaCode}
                                    onChange={handleChange}
                                    maxLength={2}
                                    placeholder="21"
                                    disabled={loading}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={9}>
                            <Form.Group className="mb-3">
                                <Form.Label>Telefone *</Form.Label>
                                <InputMask
                                    mask="99999-9999"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                    isInvalid={!!errors.phoneNumber}
                                    placeholder="99999-9999"
                                    disabled={loading}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.phoneNumber}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Endereço */}
                    <h5 className="form-section-title">Endereço</h5>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    CEP *
                                    {loadingCep && (
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            className="ms-2"
                                        />
                                    )}
                                </Form.Label>
                                <InputMask
                                    mask="99999-999"
                                    name="addressZipCode"
                                    value={formData.addressZipCode}
                                    onChange={handleChange}
                                    isInvalid={!!errors.addressZipCode}
                                    placeholder="00000-000"
                                    disabled={loading || loadingCep}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.addressZipCode}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={8}>
                            <Form.Group className="mb-3">
                                <Form.Label>Rua *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="addressStreet"
                                    value={formData.addressStreet}
                                    onChange={handleChange}
                                    isInvalid={!!errors.addressStreet}
                                    placeholder="Rua das Flores"
                                    disabled={loading}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.addressStreet}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Número *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="addressNumber"
                                    value={formData.addressNumber}
                                    onChange={handleChange}
                                    isInvalid={!!errors.addressNumber}
                                    placeholder="123"
                                    disabled={loading}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.addressNumber}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={9}>
                            <Form.Group className="mb-3">
                                <Form.Label>Complemento</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="addressComplement"
                                    value={formData.addressComplement}
                                    onChange={handleChange}
                                    placeholder="Apto 101"
                                    disabled={loading}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Bairro *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="addressNeighborhood"
                                    value={formData.addressNeighborhood}
                                    onChange={handleChange}
                                    isInvalid={!!errors.addressNeighborhood}
                                    placeholder="Centro"
                                    disabled={loading || loadingCep}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.addressNeighborhood}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Cidade *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="addressCity"
                                    value={formData.addressCity}
                                    onChange={handleChange}
                                    isInvalid={!!errors.addressCity}
                                    placeholder="Rio de Janeiro"
                                    disabled={loading || loadingCep}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.addressCity}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label>UF *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="addressState"
                                    value={formData.addressState}
                                    onChange={handleChange}
                                    isInvalid={!!errors.addressState}
                                    maxLength={2}
                                    placeholder="RJ"
                                    disabled={loading || loadingCep}
                                    style={{ textTransform: 'uppercase' }}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.addressState}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Dados do Cartão - Apenas para Crédito */}
                    {formData.paymentMethod === 'credit_card' && (
                        <>
                            <h5 className="form-section-title">
                                Dados do Cartão de Crédito
                                {cardBrand && <small className="ms-2 text-muted">({cardBrand.toUpperCase()})</small>}
                            </h5>
                            <Alert variant="info" className="mb-3">
                                <small>
                                    Os dados do seu cartão são tokenizados e enviados diretamente
                                    para o Pagar.me. Eles <strong>nunca passam pelo nosso servidor</strong>.
                                </small>
                            </Alert>

                            <Form.Group className="mb-3">
                                <Form.Label>Número do Cartão *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="cardNumber"
                                    value={formData.cardNumber}
                                    onChange={handleChange}
                                    isInvalid={!!errors.cardNumber}
                                    placeholder="0000 0000 0000 0000"
                                    disabled={loading}
                                    maxLength={19}
                                    autoComplete="cc-number"
                                    inputMode="numeric"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.cardNumber}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Nome do Titular *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="cardHolderName"
                                    value={formData.cardHolderName}
                                    onChange={handleChange}
                                    isInvalid={!!errors.cardHolderName}
                                    placeholder="JOAO DA SILVA"
                                    style={{ textTransform: 'uppercase' }}
                                    disabled={loading}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.cardHolderName}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Mês *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="cardExpMonth"
                                            value={formData.cardExpMonth}
                                            onChange={handleChange}
                                            isInvalid={!!errors.cardExpMonth}
                                            maxLength={2}
                                            placeholder="12"
                                            disabled={loading}
                                            inputMode="numeric"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Ano *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="cardExpYear"
                                            value={formData.cardExpYear}
                                            onChange={handleChange}
                                            isInvalid={!!errors.cardExpMonth}
                                            maxLength={4}
                                            placeholder="2026"
                                            disabled={loading}
                                            inputMode="numeric"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.cardExpMonth}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>CVV *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="cardCvv"
                                            value={formData.cardCvv}
                                            onChange={handleChange}
                                            isInvalid={!!errors.cardCvv}
                                            maxLength={4}
                                            placeholder="123"
                                            disabled={loading}
                                            inputMode="numeric"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.cardCvv}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Seletor de Parcelas - Apenas se o plano permitir parcelamento */}
                            {selectedPlan && selectedPlan.installments && selectedPlan.installments.length > 1 && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Parcelas *</Form.Label>
                                    <Form.Select
                                        name="installments"
                                        value={formData.installments}
                                        onChange={(e) => setFormData(prev => ({ ...prev, installments: parseInt(e.target.value) }))}
                                        isInvalid={!!errors.installments}
                                        disabled={loading}
                                    >
                                        {selectedPlan.installments.map((installment) => {
                                            const planAmount = getPlanAmount(selectedPlan) / 100;
                                            const installmentValue = planAmount / installment;
                                            return (
                                                <option key={installment} value={installment}>
                                                    {installment}x de R$ {installmentValue.toFixed(2).replace('.', ',')}
                                                    {installment === 1 ? ' (à vista)' : ''}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.installments}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-muted">
                                        Total: R$ {(getPlanAmount(selectedPlan) / 100).toFixed(2).replace('.', ',')}
                                    </Form.Text>
                                </Form.Group>
                            )}
                        </>
                    )}

                    {/* Informações Boleto */}
                    {formData.paymentMethod === 'boleto' && (
                        <Alert variant="warning" className="mb-4">
                            <h6 className="alert-heading">🧾 Pagamento via Boleto</h6>
                            <p className="mb-0">
                                Após confirmar, você receberá um <strong>boleto bancário</strong> para pagamento.
                                A compensação do boleto pode levar até 2 dias úteis após o pagamento.
                            </p>
                        </Alert>
                    )}

                    {/* Botão de Envio */}
                    <div className="d-grid gap-2">
                        <Button
                            variant="primary"
                            size="lg"
                            type="submit"
                            disabled={loading || !isFormComplete()}
                        >
                            {loading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Processando...
                                </>
                            ) : (
                                <>Assinar</>
                            )}
                        </Button>
                        {!isFormComplete() && !loading && (
                            <small className="text-muted text-center mt-2">
                                Selecione um plano para continuar e preencha todos os campos obrigatórios
                            </small>
                        )}
                    </div>
                </Form>
            </Card.Body>

            {/* Modais de Pagamento */}
            {paymentModalData && (
                <>
                    <BoletoPaymentModal
                        show={showBoletoModal}
                        onHide={() => {
                            setShowBoletoModal(false);
                            setPaymentModalData(null);
                            // Limpa dados pessoais/cartão mas mantém o plano
                            setFormData(prev => ({
                                ...prev,
                                customerName: '',
                                customerEmail: '',
                                customerDocument: '',
                                phoneAreaCode: '',
                                phoneNumber: '',
                                addressZipCode: '',
                                addressStreet: '',
                                addressNumber: '',
                                addressComplement: '',
                                addressNeighborhood: '',
                                addressCity: '',
                                addressState: '',
                                cardNumber: '',
                                cardHolderName: '',
                                cardExpMonth: '',
                                cardExpYear: '',
                                cardCvv: '',
                            }));
                            setCardBrand('');
                        }}
                        boletoUrl={paymentModalData.boletoUrl || ''}
                        subscriptionId={paymentModalData.subscriptionId}
                        amount={paymentModalData.amount}
                        dueDate={paymentModalData.dueDate}
                        isTestEnvironment={paymentModalData.isTestEnvironment}
                    />

                    <PaymentResultModal
                        show={showPaymentResultModal}
                        onHide={() => {
                            setShowPaymentResultModal(false);
                            setPaymentModalData(null);
                            // Limpa dados pessoais/cartão mas mantém o plano
                            setFormData(prev => ({
                                ...prev,
                                customerName: '',
                                customerEmail: '',
                                customerDocument: '',
                                phoneAreaCode: '',
                                phoneNumber: '',
                                addressZipCode: '',
                                addressStreet: '',
                                addressNumber: '',
                                addressComplement: '',
                                addressNeighborhood: '',
                                addressCity: '',
                                addressState: '',
                                cardNumber: '',
                                cardHolderName: '',
                                cardExpMonth: '',
                                cardExpYear: '',
                                cardCvv: '',
                            }));
                            setCardBrand('');
                        }}
                        success={paymentModalData.success || false}
                        subscriptionId={paymentModalData.subscriptionId}
                        status={paymentModalData.status}
                        message={paymentModalData.message}
                        amount={paymentModalData.amount}
                        installments={paymentModalData.installments}
                    />
                </>
            )}

            {/* Toast de Notificações */}
            <ToastNotification
                show={alert.show}
                onClose={() => setAlert({ show: false, type: 'success', message: '' })}
                message={alert.message}
                type={alert.type}
                autoHide={!loading && alert.type !== 'danger'}
                delay={alert.type === 'info' ? 3000 : 5000}
            />
        </Card>
    );
};

export default SubscriptionForm;
